import { memo, useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { TRAFFIC_HEX } from '../shared/constants';
import { useOverviewData } from './useOverviewData';

const OUTPUT_TYPES = [
  { name: 'Knowledge Products', key: 'Knowledge product' },
  { name: 'Capacity Sharing', key: 'Capacity sharing for development' },
  { name: 'Innovation Dev', key: 'Innovation development' },
  { name: 'Other Output', key: 'Other output' },
];
const OUTCOME_TYPES = [
  { name: 'Innovation Use', key: 'Innovation use' },
  { name: 'Policy Change', key: 'Policy change' },
  { name: 'Other Outcome', key: 'Other outcome' },
  { name: 'Capacity Change', key: 'Capacity change' },
  { name: 'Innovation Packages', key: 'Innovation Package' },
];
const IMPACT_TYPES = [
  { name: 'Impact Contributions', key: 'Impact contribution' },
];

const IA_CONFIG = [
  { key: 'gender', label: 'Gender', color: '#8B5CF6', angle: -90 },
  { key: 'climate', label: 'Climate', color: '#006837', angle: -18 },
  { key: 'nutrition', label: 'Nutrition', color: '#F37021', angle: 54 },
  { key: 'biodiversity', label: 'Biodiversity', color: '#14B8A6', angle: 126 },
  { key: 'poverty', label: 'Poverty', color: '#007DBC', angle: 198 },
];

const TRAFFIC_COLORS = TRAFFIC_HEX;

const SPHERE_RING_COLORS = {
  output: '#d97706',
  outcome: '#e11d48',
  impact: '#7e22ce',
};

const CX = 250;
const CY = 250;
const INNER_R = 50;
const MID_R = 95;
const OUTER_R = 130;
const PETAL_R = 148;
const MAX_PETAL_LEN = 90;

function polarToCart(angleDeg, radius) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: CX + radius * Math.cos(rad), y: CY + radius * Math.sin(rad) };
}

function petalPath(angleDeg, length, width) {
  const rad = (angleDeg * Math.PI) / 180;
  const perpRad = rad + Math.PI / 2;

  const baseStart = polarToCart(angleDeg, PETAL_R);
  const tip = polarToCart(angleDeg, PETAL_R + length);

  const halfW = width / 2;
  const lx = baseStart.x + halfW * Math.cos(perpRad);
  const ly = baseStart.y + halfW * Math.sin(perpRad);
  const rx = baseStart.x - halfW * Math.cos(perpRad);
  const ry = baseStart.y - halfW * Math.sin(perpRad);

  // Control points for the teardrop curves
  const cpDist = length * 0.6;
  const cp1x = lx + cpDist * Math.cos(rad);
  const cp1y = ly + cpDist * Math.sin(rad);
  const cp2x = rx + cpDist * Math.cos(rad);
  const cp2y = ry + cpDist * Math.sin(rad);

  return `M${lx},${ly} Q${cp1x},${cp1y} ${tip.x},${tip.y} Q${cp2x},${cp2y} ${rx},${ry} Z`;
}

const DonutTooltip = ({ active, payload }) => {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  if (d.isSpacer) return null;
  return (
    <div style={{ background: '#fff', padding: '6px 10px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '12px' }}>
      <div style={{ fontWeight: 600 }}>{d.name}</div>
      <div style={{ color: '#6b7280' }}>{d.value.toLocaleString()} results</div>
    </div>
  );
};

export const ExecutivePetalsDiagram = memo(function ExecutivePetalsDiagram() {
  const { stats, byTypeAndFunding, byImpactArea, areasOfWork, filteredResults } = useOverviewData();
  const [animated, setAnimated] = useState(false);
  const [hoveredPetal, setHoveredPetal] = useState(null);
  const containerRef = useRef(null);
  const [petalTooltip, setPetalTooltip] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(t);
  }, []);

  const byType = stats?.byType;

  // Build ring data for donut
  const outputData = useMemo(() =>
    OUTPUT_TYPES.map(t => ({ name: t.name, value: byType?.[t.key] || 0 })).filter(d => d.value > 0),
    [byType]
  );
  const outcomeData = useMemo(() =>
    OUTCOME_TYPES.map(t => ({ name: t.name, value: byType?.[t.key] || 0 })).filter(d => d.value > 0),
    [byType]
  );
  const impactData = useMemo(() =>
    IMPACT_TYPES.map(t => ({ name: t.name, value: byType?.[t.key] || 0 })).filter(d => d.value > 0),
    [byType]
  );

  const totalOutputs = outputData.reduce((s, d) => s + d.value, 0);
  const totalOutcomes = outcomeData.reduce((s, d) => s + d.value, 0);
  const totalImpact = impactData.reduce((s, d) => s + d.value, 0);
  const totalResults = totalOutputs + totalOutcomes + totalImpact;

  const iaData = useMemo(() => {
    const maxTargeted = Math.max(
      ...IA_CONFIG.map(ia => {
        const c = byImpactArea?.[ia.key] || { 0: 0, 1: 0, 2: 0 };
        return (c[2] || 0) + (c[1] || 0);
      }),
      1
    );
    return IA_CONFIG.map(ia => {
      const counts = byImpactArea?.[ia.key] || { 0: 0, 1: 0, 2: 0 };
      const principal = counts[2] || 0;
      const significant = counts[1] || 0;
      const notTargeted = counts[0] || 0;
      const total = principal + significant + notTargeted;
      const targeted = principal + significant;
      const normalizedLen = Math.max((targeted / maxTargeted) * MAX_PETAL_LEN, 15);
      const petalWidth = 28 + (targeted / maxTargeted) * 14;
      return { ...ia, principal, significant, notTargeted, total, targeted, normalizedLen, petalWidth };
    });
  }, [byImpactArea]);

  const handlePetalMove = useCallback((ia, e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setPetalTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top - 10, ia });
    setHoveredPetal(ia.key);
  }, []);

  const fmt = n => n.toLocaleString();

  const chartData = useMemo(() => {
    if (!stats) return [];
    return [
      ...outputData.map(d => ({ sphere: 'Outputs', type: d.name, count: d.value })),
      ...outcomeData.map(d => ({ sphere: 'Outcomes', type: d.name, count: d.value })),
      ...impactData.map(d => ({ sphere: 'Impact', type: d.name, count: d.value })),
    ];
  }, [stats, outputData, outcomeData, impactData]);

  if (!stats) return null;

  return (
    <ChartWrapper
      title="Executive Summary — Donut & Petals"
      info="Three-ring donut shows result distribution across CGIAR spheres (Control/Influence/Interest). Petals radiating outward represent the 5 Impact Areas, sized by the number of results with Principal or Significant targeting."
      chartData={chartData}
      sourceResults={filteredResults}
      exportFilename="executive-petals"
    >
      <div ref={containerRef} className="relative">
        <div className="flex justify-center">
          <div className="relative w-full max-w-[500px] mx-auto aspect-square">
            {/* SVG overlay for petals + labels (positioned behind Recharts) */}
            <svg viewBox="0 0 500 500" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible' }}>
              {/* Petals */}
              {iaData.map((ia, i) => {
                const isHovered = hoveredPetal === ia.key;
                const principalFrac = ia.targeted > 0 ? ia.principal / ia.targeted : 0;
                return (
                  <g
                    key={ia.key}
                    onMouseMove={e => handlePetalMove(ia, e)}
                    onMouseLeave={() => { setPetalTooltip(null); setHoveredPetal(null); }}
                    style={{ cursor: 'default' }}
                  >
                    {/* Full petal (significant + principal) */}
                    <path
                      d={petalPath(ia.angle, ia.normalizedLen, ia.petalWidth)}
                      fill={ia.color}
                      fillOpacity={isHovered ? 0.5 : 0.3}
                      stroke={ia.color}
                      strokeWidth={isHovered ? 2 : 1}
                      strokeOpacity={0.6}
                      style={{
                        transform: animated ? 'scale(1)' : 'scale(0)',
                        transformOrigin: `${CX}px ${CY}px`,
                        transition: `transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) ${0.3 + i * 0.08}s, fill-opacity 0.2s`,
                      }}
                    />
                    {/* Inner portion for principal */}
                    {principalFrac > 0 && (
                      <path
                        d={petalPath(ia.angle, ia.normalizedLen * principalFrac, ia.petalWidth * 0.7)}
                        fill={ia.color}
                        fillOpacity={isHovered ? 0.8 : 0.6}
                        stroke="none"
                        style={{
                          transform: animated ? 'scale(1)' : 'scale(0)',
                          transformOrigin: `${CX}px ${CY}px`,
                          transition: `transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) ${0.35 + i * 0.08}s, fill-opacity 0.2s`,
                        }}
                      />
                    )}
                    {/* Label at tip */}
                    {(() => {
                      const labelR = PETAL_R + ia.normalizedLen + 14;
                      const pt = polarToCart(ia.angle, labelR);
                      return (
                        <text
                          x={pt.x} y={pt.y}
                          textAnchor="middle" dominantBaseline="middle"
                          fontSize="11" fontWeight="600" fill={ia.color}
                          style={{ pointerEvents: 'none' }}
                        >
                          {ia.label}
                        </text>
                      );
                    })()}
                  </g>
                );
              })}

              {/* Sphere ring labels */}
              <text x={CX} y={CY + INNER_R + 12} textAnchor="middle" fontSize="7" fill="var(--text-tertiary, #9ca3af)">Outputs</text>
              <text x={CX} y={CY + MID_R + 10} textAnchor="middle" fontSize="7" fill="var(--text-tertiary, #9ca3af)">Outcomes</text>
              <text x={CX} y={CY + OUTER_R + 10} textAnchor="middle" fontSize="7" fill="var(--text-tertiary, #9ca3af)">Impact</text>
            </svg>

            {/* Recharts donut */}
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Tooltip content={<DonutTooltip />} />
                {/* Inner ring: Outputs */}
                <Pie data={outputData} dataKey="value" cx="50%" cy="50%" innerRadius={INNER_R - 18} outerRadius={INNER_R} isAnimationActive={false}>
                  {outputData.map((d, i) => (
                    <Cell key={d.name} fill={SPHERE_RING_COLORS.output} fillOpacity={0.6 + (i / outputData.length) * 0.3} stroke="#fff" strokeWidth={1} />
                  ))}
                </Pie>
                {/* Middle ring: Outcomes */}
                <Pie data={outcomeData} dataKey="value" cx="50%" cy="50%" innerRadius={MID_R - 20} outerRadius={MID_R} isAnimationActive={false}>
                  {outcomeData.map((d, i) => (
                    <Cell key={d.name} fill={SPHERE_RING_COLORS.outcome} fillOpacity={0.5 + (i / outcomeData.length) * 0.35} stroke="#fff" strokeWidth={1} />
                  ))}
                </Pie>
                {/* Outer ring: Impact */}
                <Pie data={impactData} dataKey="value" cx="50%" cy="50%" innerRadius={OUTER_R - 16} outerRadius={OUTER_R} isAnimationActive={false}>
                  {impactData.map(d => (
                    <Cell key={d.name} fill={SPHERE_RING_COLORS.impact} fillOpacity={0.7} stroke="#fff" strokeWidth={1} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            {/* Center label */}
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text-primary, #111827)', lineHeight: 1 }}>{fmt(totalResults)}</div>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary, #9ca3af)', marginTop: 2 }}>Results</div>
            </div>
          </div>
        </div>

        {/* Petal tooltip */}
        {petalTooltip && (
          <div
            className="absolute bg-gray-900 text-white text-xs rounded-lg px-3 py-2 pointer-events-none shadow-lg z-50"
            style={{ left: petalTooltip.x, top: petalTooltip.y, transform: 'translate(-50%, -100%)' }}
          >
            <div className="font-semibold" style={{ color: petalTooltip.ia.color }}>{petalTooltip.ia.label}</div>
            <div>Principal: {fmt(petalTooltip.ia.principal)}</div>
            <div>Significant: {fmt(petalTooltip.ia.significant)}</div>
            <div>Not Targeted: {fmt(petalTooltip.ia.notTargeted)}</div>
            <div className="mt-1 pt-1 border-t border-gray-700 text-gray-400">Total: {fmt(petalTooltip.ia.total)}</div>
          </div>
        )}
      </div>

      {/* Ring legend + petal legend */}
      <div className="flex flex-wrap justify-center gap-3 sm:gap-5 mt-2 mb-1 text-[11px] text-[var(--text-secondary,#6b7280)]">
        <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm" style={{ backgroundColor: SPHERE_RING_COLORS.output }} /> Outputs (inner)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm" style={{ backgroundColor: SPHERE_RING_COLORS.outcome }} /> Outcomes (mid)</span>
        <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm" style={{ backgroundColor: SPHERE_RING_COLORS.impact }} /> Impact (outer)</span>
      </div>
      <div className="flex justify-center gap-3 text-[10px] text-[var(--text-tertiary,#9ca3af)] mb-3">
        <span>Petals = Impact Area targeting (darker = Principal, lighter = Significant)</span>
      </div>

      {/* AoW Traffic Lights */}
      {areasOfWork.length > 0 && (
        <div className="bg-[var(--surface-secondary,#f8fafc)] rounded-xl p-4 border border-[var(--border-primary,#e5e7eb)]">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary,#6b7280)] mb-3">Areas of Work Progress</h4>
          <div className="flex flex-wrap gap-x-6 gap-y-1.5">
            {areasOfWork.map(aow => (
              <div key={aow.id} className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: TRAFFIC_COLORS[aow.ratingKey] || '#9ca3af' }} />
                <span className="text-xs text-[var(--text-primary,#111827)]">{aow.name}</span>
                <span className="text-[10px] tabular-nums text-[var(--text-tertiary,#9ca3af)]">
                  {aow.achievementPct != null ? `${Math.round(aow.achievementPct)}%` : '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </ChartWrapper>
  );
});
